import {
  FILE_TABLE_ACTION_SYNC_VERSION,
  FILE_TABLE_ACTION_SYNC_VERSION_HIS,
  FILE_TABLE_ACTION_UPLOAD_AND_SHARE_FORM,
  FILE_TABLE_ACTION_UPLOAD_AND_SHARE_HIS,
  FILE_TABLE_ACTION_UPLOAD_HIS,
  QUERY_PROGRESS_NOT_STARTED,
} from "core/consts";
import {
  Auction,
  AuctionRequest,
  FileHISStateful,
  FileParams,
  FileState,
  Filev2,
  Filev2Stateful,
  QueryProgress,
} from "core/types";
import { ProcessedFile } from "ds_legacy/components/FileUploader";
import { GeneralListAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { useImportAndUploadFilesContentHIS } from "onprem/hooks";
import { useEffect, useState } from "react";
import { useDeleteFile } from "./useDeleteFile";
import { useDownloadFile } from "./useDownloadFile";
import { useShareFile } from "./useShareFile";
import { useUploadAndShareForm } from "./useUploadAndShareForm";
import { FileUploadStates, useUploadFile } from "./useUploadFile";

export type FileFeatureData =
  | FileState
  | FileState[]
  | (FileHISStateful & { fileV2Id?: Filev2Stateful["id"] })[]
  | Filev2Stateful[]
  | ProcessedFile;

export function useFilesFeatures({
  auction,
  auctionRequest,
  disableFileShare,
  externalId,
  params,
}: {
  auction?: Auction;
  auctionRequest?: AuctionRequest;
  disableFileShare?: boolean;
  externalId?: string;
  params: FileParams;
}) {
  const [uploadStates, setUploadStates] = useState<FileUploadStates>({});
  const [action, setAction] = useState<
    GeneralListAction<FileFeatureData> | undefined
  >();
  const [mutationProgress, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  const { handleUploadFile } = useUploadFile({
    action,
    auction,
    auctionRequest,
    disableShare: disableFileShare,
    params,
    setAction,
    setUploadStates,
    uploadStates,
  });
  const { handleShareFile } = useShareFile({
    action: action as GeneralListAction<Filev2> | undefined,
    params,
    setAction,
    setProgress,
  });
  const { handleDeleteFile } = useDeleteFile({
    action: action as GeneralListAction<Filev2> | undefined,
    params,
    setAction,
    setProgress,
  });
  useDownloadFile({
    action: action as GeneralListAction<Filev2> | undefined,
    externalId: externalId || "",
    setAction,
    setProgress,
  });

  useImportAndUploadFilesContentHIS({
    action,
    externalId: externalId || "",
    filesHIS: action?.data as FileHISStateful[],
    handleDeleteFile,
    handleUploadFile,
    setAction,
    skip:
      !externalId ||
      ![
        FILE_TABLE_ACTION_UPLOAD_HIS,
        FILE_TABLE_ACTION_UPLOAD_AND_SHARE_HIS,
        FILE_TABLE_ACTION_SYNC_VERSION_HIS,
      ].includes(action?.type as any) ||
      !action?.data,
  });

  useUploadAndShareForm({
    action,
    externalId: externalId || "",
    handleDeleteFile,
    handleUploadFile,
    setAction,
    setUploadStates,
    skip:
      !externalId ||
      ![
        FILE_TABLE_ACTION_UPLOAD_AND_SHARE_FORM,
        FILE_TABLE_ACTION_SYNC_VERSION,
      ].includes(action?.type as any) ||
      !action?.data,
  });

  async function handleReset() {
    setAction(undefined);
    setProgress(QUERY_PROGRESS_NOT_STARTED);
  }

  useEffect(() => {
    if (action?.type) {
      setProgress(QUERY_PROGRESS_NOT_STARTED);
    }
  }, [action?.type]);

  return {
    action,
    handleDeleteFile,
    handleReset,
    handleShareFile,
    handleUploadFile,
    mutationProgress,
    setAction,
    setProgress,
    setUploadStates,
    uploadStates,
  };
}
